import { useState, useRef, useCallback, useEffect } from 'react';
import SwiperType from 'swiper';

interface UseSwiperSetupProps {
  onInit?: (swiper: SwiperType) => void;
  onSwiper?: (swiper: SwiperType) => void;
  withThumbs?: boolean;
}

interface UseSwiperSetupReturn {
  init: boolean;
  swiperRef: React.MutableRefObject<SwiperType | undefined>;
  navigationPrevRef: React.RefObject<HTMLDivElement>;
  navigationNextRef: React.RefObject<HTMLDivElement>;
  thumbsSwiper: SwiperType | undefined;
  handleInit: (swiper: SwiperType) => void;
  handleOnSwiper: (swiper: SwiperType) => void;
}

/**
 * Custom hook for managing Swiper slider initialization and setup.
 *
 * Handles:
 * - Swiper instance initialization tracking
 * - Navigation button refs for custom arrow controls
 * - Thumbnail slider setup (for gallery with thumbnails)
 * - ResizeObserver for responsive updates when container size changes
 * - Cleanup of observers on unmount (React lifecycle) and destroy (Swiper lifecycle)
 *
 * @param {UseSwiperSetupProps} props - Configuration options
 * @param {Function} props.onInit - Callback when Swiper initializes
 * @param {Function} props.onSwiper - Callback when Swiper instance is created
 * @param {boolean} props.withThumbs - Enable thumbnail slider functionality
 *
 * @returns {UseSwiperSetupReturn} Refs, state, and handlers for Swiper setup
 *
 * @example
 * ```tsx
 * const { swiperRef, navigationPrevRef, navigationNextRef, handleInit, handleOnSwiper } = useSwiperSetup({
 *   onInit: (swiper) => console.log('Initialized'),
 *   withThumbs: true
 * });
 *
 * <Swiper
 *   onInit={handleInit}
 *   onSwiper={handleOnSwiper}
 *   navigation={{
 *     prevEl: navigationPrevRef.current,
 *     nextEl: navigationNextRef.current
 *   }}
 * />
 * ```
 */
export const useSwiperSetup = ({ onInit, onSwiper, withThumbs }: UseSwiperSetupProps): UseSwiperSetupReturn => {
  const [init, setInit] = useState(false);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>();
  const swiperRef = useRef<SwiperType>();
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  // Track which Swiper instance has destroy handler to prevent duplicate registrations
  const destroyHandlerSwiperRef = useRef<SwiperType | null>(null);

  // Clean up ResizeObserver on unmount (React lifecycle)
  // This ensures cleanup even if Swiper's destroy event doesn't fire
  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  const setupResizeObserver = useCallback((swiper: SwiperType) => {
    if (typeof window === 'undefined' || !window.ResizeObserver) return;

    // Disconnect existing observer before creating new one
    // Prevents memory leaks if setupResizeObserver is called multiple times
    resizeObserverRef.current?.disconnect();

    const resizeObserver = new ResizeObserver(() => {
      if (swiper && !swiper.destroyed) {
        swiper.update();
        swiper.updateSlides();
        swiper.updateProgress();
        swiper.updateSlidesClasses();
      }
    });

    const container = swiper.el.parentElement;
    if (container) {
      resizeObserver.observe(container);
      resizeObserverRef.current = resizeObserver;
    }
  }, []);

  const handleInit = useCallback(
    (swiper: SwiperType) => {
      setInit(true);
      onInit?.(swiper);

      // Register destroy handler only once per Swiper instance to prevent duplicate registrations.
      // This guards against edge cases where handleInit might be called multiple times
      // (e.g., Swiper destroyed and recreated while component remains mounted).
      if (destroyHandlerSwiperRef.current !== swiper) {
        destroyHandlerSwiperRef.current = swiper;
        swiper.on('destroy', () => {
          resizeObserverRef.current?.disconnect();
          resizeObserverRef.current = null;
          // Clear the ref so a new Swiper instance can register its handler
          if (destroyHandlerSwiperRef.current === swiper) {
            destroyHandlerSwiperRef.current = null;
          }
        });
      }
    },
    [onInit],
  );

  const handleOnSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      onSwiper?.(swiper);

      if (withThumbs) {
        setThumbsSwiper(swiper);
      }

      swiper.update();
      setupResizeObserver(swiper);
    },
    [onSwiper, withThumbs, setupResizeObserver],
  );

  return {
    init,
    swiperRef,
    navigationPrevRef,
    navigationNextRef,
    thumbsSwiper,
    handleInit,
    handleOnSwiper,
  };
};
